import { CONFIG } from './config.js';
import {
  BOUNDS,
  canPlace,
  chooseAiMove,
  createGame,
  currentPlayer,
  isOver,
  placeSite,
  scores,
  turnsLeft,
} from './game.js';
import { buildVoronoi } from './voronoi.js';

const AI_DELAY_FRAMES = 24;

/**
 * p5 instance-mode sketch. Everything the game needs lives on `game`;
 * this module only reads it and draws.
 *
 * @param {import('p5')} p
 */
export function sketch(p) {
  let game = createGame();
  let aiCountdown = 0;

  p.setup = () => {
    p.createCanvas(CONFIG.width, CONFIG.height);
    p.textFont('system-ui, sans-serif');
  };

  p.draw = () => {
    p.background('#0b0f19');

    drawCells();
    drawSites();
    drawPreview();
    drawHud();

    stepAi();
  };

  p.mousePressed = () => {
    if (isOver(game)) return;
    if (!CONFIG.players[currentPlayer(game)].human) return;
    placeSite(game, p.mouseX, p.mouseY);
    aiCountdown = AI_DELAY_FRAMES;
  };

  p.keyPressed = () => {
    if (p.key === 'r' || p.key === 'R') {
      game = createGame();
      aiCountdown = 0;
    }
  };

  function stepAi() {
    if (isOver(game) || CONFIG.players[currentPlayer(game)].human) return;
    if (aiCountdown-- > 0) return;

    const move = chooseAiMove(game);
    if (move) placeSite(game, move.x, move.y);
    aiCountdown = AI_DELAY_FRAMES;
  }

  function drawCells() {
    if (game.sites.length === 0) return;

    const voronoi = buildVoronoi(game.sites, BOUNDS);
    p.strokeWeight(1.5);

    for (let i = 0; i < game.sites.length; i++) {
      const cell = voronoi.cellPolygon(i);
      if (!cell) continue;

      const player = CONFIG.players[game.sites[i].player];
      p.fill(p.color(player.fill + '55'));
      p.stroke(p.color(player.fill + 'cc'));

      p.beginShape();
      // cellPolygon repeats the first point at the end; skip the duplicate.
      for (let j = 0; j < cell.length - 1; j++) p.vertex(cell[j][0], cell[j][1]);
      p.endShape(p.CLOSE);
    }
  }

  function drawSites() {
    p.noStroke();
    for (const site of game.sites) {
      const player = CONFIG.players[site.player];
      p.fill(player.fill);
      p.circle(site.x, site.y, 11);
      p.fill(player.accent);
      p.circle(site.x, site.y, 4);
    }
  }

  function drawPreview() {
    if (isOver(game) || !CONFIG.players[currentPlayer(game)].human) return;

    const ok = canPlace(game, p.mouseX, p.mouseY);
    const player = CONFIG.players[currentPlayer(game)];
    p.noFill();
    p.strokeWeight(2);
    p.stroke(ok ? player.fill : '#6b7280');
    p.circle(p.mouseX, p.mouseY, CONFIG.minSiteDistance);
  }

  function drawHud() {
    const totals = scores(game);
    const boardArea = (BOUNDS[2] - BOUNDS[0]) * (BOUNDS[3] - BOUNDS[1]);

    p.noStroke();
    p.fill('#111827');
    p.rect(0, 0, CONFIG.width, CONFIG.hudHeight);

    // Share-of-territory bar along the bottom edge of the HUD.
    let x = 0;
    for (let i = 0; i < CONFIG.players.length; i++) {
      const w = (totals[i] / boardArea) * CONFIG.width;
      p.fill(CONFIG.players[i].fill);
      p.rect(x, CONFIG.hudHeight - 4, w, 4);
      x += w;
    }

    p.textSize(15);
    p.textAlign(p.LEFT, p.CENTER);
    let tx = 18;
    for (let i = 0; i < CONFIG.players.length; i++) {
      const pct = ((totals[i] / boardArea) * 100).toFixed(1);
      p.fill(CONFIG.players[i].fill);
      p.circle(tx, CONFIG.hudHeight / 2 - 2, 10);
      p.fill('#e5e7eb');
      p.text(`${CONFIG.players[i].name} ${pct}%`, tx + 12, CONFIG.hudHeight / 2 - 2);
      tx += 150;
    }

    p.textAlign(p.RIGHT, p.CENTER);
    p.fill('#9ca3af');
    const status = isOver(game)
      ? `${winnerLabel(totals)} — press R to restart`
      : `${CONFIG.players[currentPlayer(game)].name} to move · ${turnsLeft(game)} left`;
    p.text(status, CONFIG.width - 18, CONFIG.hudHeight / 2 - 2);
  }

  function winnerLabel(totals) {
    const best = Math.max(...totals);
    const winners = CONFIG.players.filter((_, i) => totals[i] === best);
    return winners.length > 1 ? 'Draw' : `${winners[0].name} wins`;
  }
}
